import { useCallback, useContext, useEffect, useRef } from 'react';

import { usePublish } from '@atlaskit/rovo-triggers/main';

import { BackBehaviorOverrideContext } from './BackBehaviorOverrideContext';

/**
 * Publishes the `insights-chat-exited` rovo-trigger when the user leaves a seeded
 * Rovo Insight chat, letting carousel surfaces clear their selected-card highlight.
 *
 * A seeded-insight chat is identified by a {@link BackBehaviorOverride} with
 * `source: 'pulse'`, set when an insight is opened and torn down on every exit path.
 * Watching the override transition `pulse -> not-pulse` is a single signal for "no
 * longer in an insight chat". Switching directly between two insight chats keeps
 * `source: 'pulse'`, so it intentionally does not emit.
 */
export const InsightsChatExitedPublisher = (): null => {
	const { override } = useContext(BackBehaviorOverrideContext);
	const publish = usePublish('ai-mate');
	const wasInInsightChat = useRef(false);
	// Latest-publish ref so the unmount cleanup calls the current publish fn.
	const publishRef = useRef(publish);
	useEffect(() => {
		publishRef.current = publish;
	}, [publish]);

	const isInInsightChat = override?.source === 'pulse';

	const publishExited = useCallback(() => {
		publishRef.current({
			type: 'insights-chat-exited',
			source: 'ai-mate',
			// Prevent this close signal from reopening chat through ChatOpenerSubscriber.
			openChat: false,
			data: {},
		});
	}, []);

	// Handles in-place exits where the component stays mounted and re-renders.
	useEffect(() => {
		if (wasInInsightChat.current && !isInInsightChat) {
			publishExited();
		}
		wasInInsightChat.current = isInInsightChat;
	}, [isInInsightChat, publishExited]);

	// Handles teardown while still in an insight chat (e.g. chat panel closes).
	useEffect(
		() => () => {
			if (wasInInsightChat.current) {
				publishExited();
			}
		},
		[publishExited],
	);

	return null;
};
